import { useEffect, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { ChevronLeft, Download, Copy, CheckCircle2, ChefHat, ChevronDown } from 'lucide-react'
import { shoppingApi, type ShoppingList, type ShoppingListItem } from '../services/api'
import { exportToPDF, copyToClipboard } from '../services/export'
import { useAuth } from '../contexts/useAuth'
import toast from 'react-hot-toast'

export default function ShoppingMode() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const location = useLocation()
    const { isAuthenticated } = useAuth()
    const [list, setList] = useState<ShoppingList | null>(location.state?.list || null)
    const [loading, setLoading] = useState(!list)
    const [showRecipes, setShowRecipes] = useState(false)

    useEffect(() => {
        if (!id) return

        if (list) {
            Promise.resolve().then(() => {
                setLoading(false)
            })
            return
        }

        let active = true
        Promise.resolve().then(() => {
            if (active) setLoading(true)
        })
        shoppingApi.get(id)
            .then(l => {
                if (active) {
                    setList(l)
                    shoppingApi.updateStatus(id, 'shopping')
                }
            })
            .catch(() => {
                if (active) toast.error('Liste introuvable')
            })
            .finally(() => {
                if (active) setLoading(false)
            })
        return () => {
            active = false
        }
    }, [id, list])

    useEffect(() => {
        if (!isAuthenticated && list) {
            const handleBeforeUnload = (e: BeforeUnloadEvent) => {
                e.preventDefault()
                e.returnValue = ''
            }
            window.addEventListener('beforeunload', handleBeforeUnload)
            return () => window.removeEventListener('beforeunload', handleBeforeUnload)
        }
    }, [isAuthenticated, list])

    const toggleItem = async (item: ShoppingListItem) => {
        if (!list) return
        if (isAuthenticated) {
            const updated = await shoppingApi.updateItem(list.id, item.id, { checked: !item.checked })
            setList(prev => prev ? { ...prev, items: prev.items.map(i => i.id === item.id ? { ...i, ...updated } : i) } : prev)
        } else {
            setList(prev => prev ? { ...prev, items: prev.items.map(i => i.id === item.id ? { ...i, checked: !i.checked } : i) } : prev)
        }
    }

    const fmtQty = (qty: number, unit: string) => {
        const q = qty % 1 === 0 ? qty.toString() : qty.toFixed(1)
        const labels: Record<string, string> = { cnt: 'p.', g: 'g', ml: 'ml' }
        return `${q} ${labels[unit] || unit}`
    }

    const checkedCount = list?.items.filter(i => i.checked || i.owned).length ?? 0
    const total = list?.items.length ?? 0
    const progress = total > 0 ? (checkedCount / total) * 100 : 0

    if (loading) return (
        <div style={{ padding: '48px 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[1, 2, 3, 4].map(i => <div key={i} className="skeleton" style={{ height: 64, borderRadius: 14 }} />)}
        </div>
    )

    if (!list) return null

    const remaining = list.items.filter(i => !i.checked && !i.owned)
    const done = list.items.filter(i => i.checked || i.owned)
    const allDone = remaining.length === 0

    return (
        <div>
            {/* Top bar */}
            <div className="page-header" style={{ justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button className="btn btn-ghost btn-sm btn-icon" onClick={() => {
                        if (!isAuthenticated && !confirm('Attention : votre liste n\'est pas sauvegardée et sera perdue. Quitter ?')) {
                            return
                        }
                        navigate('/app')
                    }}>
                        <ChevronLeft size={20} />
                    </button>
                    <div>
                        <h1 style={{ fontSize: '1.1rem' }}>{list.name}</h1>
                        <p style={{ fontSize: '0.78rem', color: 'var(--text-3)' }}>{checkedCount} / {total} articles</p>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-ghost btn-sm btn-icon" onClick={() => { copyToClipboard(list); toast.success('Copié !') }}>
                        <Copy size={16} />
                    </button>
                    <button className="btn btn-ghost btn-sm btn-icon" onClick={() => exportToPDF(list)}>
                        <Download size={16} />
                    </button>
                </div>
            </div>

            {/* Progress bar */}
            <div className="progress-bar" style={{ marginBottom: 24 }}>
                <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
            </div>

            {/* Selected recipes summary */}
            {list.recipes && list.recipes.length > 0 && (
                <div className="card" style={{ marginBottom: 20, padding: '14px 16px' }}>
                    <div
                        style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => setShowRecipes(!showRecipes)}
                    >
                        <ChefHat size={16} color="var(--primary)" />
                        <span style={{ fontWeight: 600, fontSize: '0.9rem', flex: 1 }}>Plats sélectionnés ({list.recipes.length})</span>
                        <ChevronDown
                            size={16}
                            style={{
                                transform: showRecipes ? 'rotate(180deg)' : 'none',
                                transition: 'transform 0.2s',
                                color: 'var(--text-3)'
                            }}
                        />
                    </div>
                    {showRecipes && (
                        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div className="divider" style={{ margin: '0' }} />
                            {list.recipes.map((r, i) => (
                                <div key={r.id || i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                                    <span style={{ color: 'var(--text)' }}>{r.name}</span>
                                    <span className="badge badge-gray" style={{ fontSize: '0.75rem', textTransform: 'capitalize' }}>
                                        {r.category === 'dessert' ? 'Dessert' : 'Plat'}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* All done message */}
            {allDone && (
                <div className="celebrate" style={{
                    textAlign: 'center',
                    padding: '36px 24px',
                    marginBottom: 16,
                    background: 'var(--success-bg)',
                    border: '1px solid rgba(16, 185, 129, 0.25)',
                    borderRadius: 'var(--radius-lg)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                }}>
                    <CheckCircle2 size={48} color="var(--success)" style={{ margin: '0 auto 12px' }} />
                    <h2 style={{ fontSize: '1.4rem', fontWeight: 700 }}>Courses terminées !</h2>
                    <p style={{ color: 'var(--text-2)', marginTop: 8, fontSize: '0.9rem' }}>Tous les articles ont été cochés.</p>
                    <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => { 
                        if (isAuthenticated) shoppingApi.updateStatus(list.id, 'done'); 
                        navigate('/app'); 
                    }}>
                        Terminer
                    </button>
                </div>
            )}

            {/* Remaining items */}
            {remaining.length > 0 && (
                <>
                    <p className="section-title">À prendre ({remaining.length})</p>
                    {remaining.map((item, idx) => (
                        <div
                            key={item.id}
                            className="list-item"
                            style={{
                                cursor: 'pointer',
                                animation: `slideUp 0.3s ease ${idx * 0.04}s both`,
                            }}
                            onClick={() => toggleItem(item)}
                        >
                            <div className={`checkbox ${item.checked ? 'checked' : ''}`}>
                                {item.checked && '✓'}
                            </div>
                            <div className="list-item-info">
                                <div className="list-item-name">{item.name}</div>
                                <div className="list-item-qty">{fmtQty(item.required_quantity, item.unit)}</div>
                            </div>
                        </div>
                    ))}
                </>
            )}

            {/* Done items */}
            {done.length > 0 && (
                <>
                    <p className="section-title">Récupérés ({done.length})</p>
                    {done.map(item => (
                        <div key={item.id} className={`list-item ${item.owned ? 'owned' : 'checked'}`} style={{ cursor: 'pointer', opacity: 0.45 }} onClick={() => !item.owned && toggleItem(item)}>
                            <div className={`checkbox ${item.owned ? 'owned' : 'checked'}`}>✓</div>
                            <div className="list-item-info">
                                <div className="list-item-name" style={{ textDecoration: 'line-through' }}>{item.name}</div>
                                <div className="list-item-qty">{item.owned ? 'En stock' : fmtQty(item.required_quantity, item.unit)}</div>
                            </div>
                        </div>
                    ))}
                </>
            )}
        </div>
    )
}
